import {
  Action,
  Alert,
  Badge,
  Card,
  ConfirmModal,
  Icon,
  Menu,
  Page,
  Disclosure,
  SimpleSummary,
} from 'corso-admin-ui';
import { CrewMerchantApi } from 'corso-types';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import DescriptionList, { Description } from '~/components/DescriptionList';
import EmptyStateAction from '~/components/EmptyStateAction';
import { useFilterDefinition } from '~/components/searchTable/searchControlBar/useFilterDefinition';
import { useCustomFields } from '~/features/settings/hooks/useCustomFields';
import {
  useAllReportDefinitions,
  useReportDefinitionDelete,
  useReportDefinitionRun,
} from '~/features/settings/hooks/useReportDefinitions';
import { useStoreId } from '~/providers/StoreUserProvider';
import { useUser } from '~/providers/UserProvider';
import {
  exportColumnDisplayName,
  filtersToDescriptions,
} from '~/utils/dataExport';
import { formatSchedule } from './dataExportFormUtils';

type Definition = CrewMerchantApi.ResponseBody<
  '/:storeId/reports/:entity/definitions/:definitionId',
  'get'
>;

function RunAction({ definition }: { definition: Definition }) {
  const { mutate: runDefinition, isPending } = useReportDefinitionRun();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const user = useUser();

  return (
    <>
      <Action
        variant="primary"
        icon="download"
        accessibilityLabel={`Run ${definition.name}`}
        loading={isPending}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setShowConfirmation(true);
        }}
      >
        Run Export
      </Action>
      <ConfirmModal
        title={`Run ${definition.name}?`}
        prompt={
          <div>
            <Alert
              variant="info"
              heading={`Your export will be emailed to ${user.email}`}
            />
          </div>
        }
        confirmText="Run"
        cancelText="Cancel"
        variant="primary"
        show={showConfirmation}
        onConfirm={() => {
          runDefinition(definition.id);
          setShowConfirmation(false);
        }}
        onCancel={() => setShowConfirmation(false)}
      />
    </>
  );
}

function EditDeleteMenu({
  definition,
  editTo,
}: {
  definition: Definition;
  editTo: { pathname: string; search: string };
}) {
  const { mutate: deleteDefinition, isPending } = useReportDefinitionDelete();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  return (
    <>
      <Menu
        align="end"
        buttonAs={
          <Action
            icon="overflowMenu"
            accessibilityLabel={`Actions for ${definition.name}`}
            variant="ghost"
          />
        }
      >
        <Menu.ItemLink to={editTo}>
          <Icon icon="edit" />
          Edit
        </Menu.ItemLink>
        <Menu.ItemButton onClick={() => setShowConfirmDelete(true)}>
          <Icon icon="remove" />
          Delete
        </Menu.ItemButton>
      </Menu>
      <ConfirmModal
        title="Delete Data Export Template"
        prompt={`Are you sure you want to delete ${definition.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        show={showConfirmDelete}
        loading={isPending}
        onConfirm={() => {
          deleteDefinition({ entity: definition.kind, id: definition.id });
          setShowConfirmDelete(false);
        }}
        onCancel={() => setShowConfirmDelete(false)}
      />
    </>
  );
}

function DataExportItem({
  definition,
  returnTo,
}: {
  definition: Definition;
  returnTo: string;
}) {
  const filterDefinition = useFilterDefinition(definition.kind);
  const { data: customFields = [] } = useCustomFields();

  const customFieldLabel = (id: number) =>
    customFields.find((cf) => cf.id === id)?.displayName ?? `#${id}`;

  const descriptions: Description[] = filtersToDescriptions(
    definition.filters,
    filterDefinition,
  );

  descriptions.push({
    term: 'Columns',
    details: (
      <div className="flex flex-wrap gap-1">
        {definition.jsonDefinition.columns.map((c) => (
          <Badge key={c}>{exportColumnDisplayName[c]}</Badge>
        ))}
      </div>
    ),
  });

  if (definition.kind === 'Return' || definition.kind === 'Warranty') {
    const { lineItemCustomFieldIds, inspectionCustomFieldIds } =
      definition.jsonDefinition;
    if (lineItemCustomFieldIds.length > 0) {
      descriptions.push({
        term: 'Line item custom fields',
        details: lineItemCustomFieldIds.map(customFieldLabel).join(', '),
      });
    }
    if (inspectionCustomFieldIds.length > 0) {
      descriptions.push({
        term: 'Inspection custom fields',
        details: inspectionCustomFieldIds.map(customFieldLabel).join(', '),
      });
    }
  }

  if (definition.kind === 'Registration') {
    const { registrationCustomFieldIds } = definition.jsonDefinition;
    if (registrationCustomFieldIds.length > 0) {
      descriptions.push({
        term: 'Registration custom fields',
        details: registrationCustomFieldIds.map(customFieldLabel).join(', '),
      });
    }
  }

  const scheduleDescription = formatSchedule({
    frequency: definition.frequency,
    exportDay: definition.exportDay,
    exportEmails: definition.exportEmails,
  });

  if (scheduleDescription) {
    descriptions.push({ term: 'Schedule', details: scheduleDescription });
  }

  const editTo = {
    pathname: `edit/${definition.id}`,
    search: new URLSearchParams({
      entity: definition.kind,
      returnTo,
    }).toString(),
  };

  return (
    <Disclosure
      renderSummary={
        <SimpleSummary>
          <div className="flex w-full flex-row items-center justify-between gap-2">
            <div className="flex flex-row items-center gap-2">
              <p className="truncate text-left text-sm font-medium text-balance text-neutral-700">
                {definition.name}
              </p>
              <Badge>{definition.kind}</Badge>
            </div>
            <div className="flex flex-row items-center gap-2">
              <EditDeleteMenu definition={definition} editTo={editTo} />
              <RunAction definition={definition} />
            </div>
          </div>
        </SimpleSummary>
      }
    >
      <div className="mt-1 px-2">
        <DescriptionList descriptions={descriptions} />
      </div>
    </Disclosure>
  );
}

export default function DataExportSettingsPage() {
  const storeId = useStoreId();
  const location = useLocation();
  const queries = useAllReportDefinitions();
  const returnTo = location.pathname;

  const definitions = queries.flatMap((q) => q.data);
  const sorted = [...definitions].sort((a, b) => a.name.localeCompare(b.name));
  const isEmpty = sorted.length === 0;

  const createTo = {
    pathname: 'create',
    search: new URLSearchParams({
      returnTo: `/${storeId}/settings/data-export`,
    }).toString(),
  };

  return (
    <Page
      title="Data Export Templates"
      primaryAction={{
        content: 'Add Data Export Template',
        icon: 'add',
        to: createTo,
      }}
    >
      <Card>
        {isEmpty ? (
          <EmptyStateAction.Link
            to={createTo}
            icon={<Icon icon="download" />}
            label="Create a data export template"
            description="Data export templates allow you to save export configurations for reuse."
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {sorted.map((definition) => (
              <li key={`${definition.kind}-${definition.id}`}>
                <DataExportItem definition={definition} returnTo={returnTo} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Page>
  );
}
